import React, { useCallback, useEffect, useState } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import axios from 'axios';
import { ENDPOINT } from 'constants';
import { CircularProgress, Switch, Card, Chip, Tooltip, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Notify } from 'notiflix';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import EmailTemplateModal from './EmailTemplateModal';
import EmailTemplatePreviewModal from './EmailTemplatePreviewModal';
import OverView from './OverView';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const EMPTY_FORM = {
  name: '',
  templateKey: '',
  reminderType: '',
  subject: '',
  body: '',
  bodyFormat: 'text',
  smsBody: '',
  availableVariables: '',
  description: '',
};

const EmailTemplates = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const canRead = usePermission('email-management', 'read');
  const canCreate = usePermission('email-management', 'create');
  const canUpdate = usePermission('email-management', 'update');
  const canDelete = usePermission('email-management', 'delete');
  const [view, setView] = useState('overview');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/reminder-templates`, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      if (data.success) {
        setTemplates(data.data);
      } else {
        Notify.failure('Failed to fetch templates');
      }
    } catch (err) {
      console.error(err);
      Notify.failure('Error fetching templates');
    } finally {
      setLoading(false);
    }
  }, [user?.accessToken, canRead]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await axios.patch(
        `${ENDPOINT}api/v1/admin/reminder-templates/${id}/status`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } },
      );
      Notify.success('Status updated');
      // Optimistic update — avoids a full refetch
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? { ...t, isActive: !t.isActive } : t)),
      );
    } catch (err) {
      console.error(err);
      Notify.failure('Failed to update status');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      const template = templates.find((t) => t.id === id);
      await axios.delete(
        `${ENDPOINT}api/v1/admin/reminder-templates/${id}?label=${encodeURIComponent(template?.name || '')}`,
        { headers: { Authorization: `Bearer ${user?.accessToken}` } },
      );
      Notify.success('Template deleted');
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error(err);
      Notify.failure('Failed to delete template');
    }
  };

  const openCreateModal = () => {
    setSelectedTemplate(null);
    setIsEdit(false);
    setModalOpen(true);
  };

  const openEditModal = (template) => {
    setSelectedTemplate(template);
    setIsEdit(true);
    setModalOpen(true);
  };

  const openPreviewModal = (template) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const handleSubmitTemplate = async (values) => {
    try {
      if (isEdit && selectedTemplate) {
        await axios.put(
          `${ENDPOINT}api/v1/admin/reminder-templates/${selectedTemplate.id}`,
          values,
          { headers: { Authorization: `Bearer ${user?.accessToken}` } },
        );
        Notify.success('Template updated successfully');
      } else {
        await axios.post(
          `${ENDPOINT}api/v1/admin/reminder-templates`,
          values,
          { headers: { Authorization: `Bearer ${user?.accessToken}` } },
        );
        Notify.success('Template created successfully');
      }
      fetchTemplates();
      setModalOpen(false);
    } catch (err) {
      console.error(err);
      Notify.failure(isEdit ? 'Failed to update template' : 'Failed to create template');
    }
  };

  // Each column's `accessor` must resolve to a plain, searchable string — the table's
  // global text filter runs against that raw value, not against whatever `Cell` renders.
  // Previously every accessor held pre-built JSX, so search compared against
  // "[object Object]" for every row and never matched anything. `Cell` renders the rich
  // markup for display; `accessor` supplies the plain text search/sort actually use.
  const columns = [
    { Header: '#', accessor: 'sr', width: '4%', disableGlobalFilter: true },
    {
      Header: 'Name',
      accessor: 'name',
      width: '20%',
      Cell: ({ row }) => (
        <MDBox>
          <MDTypography variant="button" fontWeight="medium" display="block">
            {row.original.name}
          </MDTypography>
          {row.original.description && (
            <MDTypography variant="caption" color="secondary">
              {row.original.description}
            </MDTypography>
          )}
        </MDBox>
      ),
    },
    {
      Header: 'Template Key',
      accessor: 'templateKey',
      width: '20%',
      Cell: ({ row }) => (
        <Tooltip title="System identifier — do not rename">
          <Chip
            label={row.original.templateKey}
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
          />
        </Tooltip>
      ),
    },
    {
      Header: 'Reminder Type',
      accessor: 'reminderType',
      width: '13%',
      Cell: ({ row }) => <Chip label={row.original.reminderType} size="small" color="info" />,
    },
    {
      Header: 'Channels',
      accessor: 'channels',
      width: '12%',
      Cell: ({ row }) => {
        const template = row.original._template;
        return (
          <MDBox display="flex" gap={0.5}>
            {(template.subject || template.body) && (
              <Chip label="Email" size="small" color="info" variant="outlined" />
            )}
            {template.smsBody && (
              <Chip label="SMS" size="small" color="success" variant="outlined" />
            )}
            {!(template.subject || template.body) && !template.smsBody && (
              <MDTypography variant="caption" color="secondary" fontStyle="italic">
                None
              </MDTypography>
            )}
          </MDBox>
        );
      },
    },
    {
      Header: 'Subject',
      accessor: 'subject',
      width: '20%',
      Cell: ({ row }) => (
        <MDTypography
          variant="caption"
          sx={{
            fontStyle: row.original.subject ? 'normal' : 'italic',
            color: row.original.subject ? 'inherit' : 'text.secondary',
          }}
        >
          {row.original.subject || '(no subject)'}
        </MDTypography>
      ),
    },
    {
      Header: 'Active',
      accessor: 'isActive',
      align: 'center',
      width: '7%',
      disableGlobalFilter: true,
      Cell: ({ row }) => {
        const template = row.original._template;
        return (
          <Switch
            checked={template.isActive}
            onChange={() => handleToggleStatus(template.id, template.isActive)}
            disabled={!canUpdate}
            color="success"
            size="small"
          />
        );
      },
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      align: 'center',
      width: '11%',
      disableGlobalFilter: true,
      Cell: ({ row }) => {
        const template = row.original._template;
        return (
          <MDBox display="flex" gap={0.5} justifyContent="center" alignItems="center">
            <Tooltip title="Preview — see how this email/SMS looks">
              <IconButton size="small" color="info" onClick={() => openPreviewModal(template)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canUpdate && (
              <MDButton size="small" variant="outlined" color="info" onClick={() => openEditModal(template)}>
                Edit
              </MDButton>
            )}
            {canDelete && (
              <MDButton size="small" variant="outlined" color="error" onClick={() => handleDeleteTemplate(template.id)}>
                Delete
              </MDButton>
            )}
          </MDBox>
        );
      },
    },
  ];

  const rows = templates.map((template, index) => ({
    sr: index + 1,
    name: [template.name, template.description].filter(Boolean).join(' '),
    templateKey: template.templateKey || '',
    reminderType: template.reminderType || '',
    channels: [
      (template.subject || template.body) ? 'Email' : '',
      template.smsBody ? 'SMS' : '',
    ].filter(Boolean).join(' '),
    subject: template.subject || '',
    isActive: template.isActive ? 'Active' : 'Inactive',
    actions: '',
    // Full raw template — Cell renderers above read from this, keeping the accessor
    // fields above as plain searchable strings.
    _template: template,
  }));

  // Build initialValues for the modal — includes all fields
  const modalInitialValues = isEdit && selectedTemplate
    ? {
        name: selectedTemplate.name || '',
        templateKey: selectedTemplate.templateKey || '',
        reminderType: selectedTemplate.reminderType || '',
        subject: selectedTemplate.subject || '',
        body: selectedTemplate.body || '',
        bodyFormat: selectedTemplate.bodyFormat || 'text',
        smsBody: selectedTemplate.smsBody || '',
        availableVariables: selectedTemplate.availableVariables || '',
        description: selectedTemplate.description || '',
      }
    : EMPTY_FORM;

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to Email Templates. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
            display="flex" justifyContent="space-between" alignItems="center"
          >
            <MDBox>
              <MDTypography variant="h6" color="white">Email Templates</MDTypography>
              <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                {templates.filter((t) => t.isActive).length} active of {templates.length} total
              </MDTypography>
            </MDBox>
            <MDBox display="flex" gap={1}>
              <MDButton
                variant={view === 'overview' ? 'contained' : 'outlined'}
                color="white"
                onClick={() => setView('overview')}
              >
                Overview
              </MDButton>
              <MDButton
                variant={view === 'templates' ? 'contained' : 'outlined'}
                color="white"
                onClick={() => setView('templates')}
              >
                Templates
              </MDButton>
              {view === 'templates' && canCreate && (
                <MDButton variant="contained" onClick={openCreateModal}>
                  Add New Template
                </MDButton>
              )}
              <MDButton
                variant="outlined"
                color="white"
                onClick={() => navigate('/email-branding')}
              >
                Email Branding
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox p={3}>
            {view === 'overview' ? (
              <OverView templates={templates} />
            ) : loading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }}
                showTotalEntries
                canSearch
              />
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />

      <EmailTemplateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitTemplate}
        initialValues={modalInitialValues}
        isEdit={isEdit}
      />

      <EmailTemplatePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        template={previewTemplate}
      />
    </DashboardLayout>
  );
};

export default EmailTemplates;